import QRCode from "qrcode";
import { useMemo } from "react";
import type { ReactNode } from "react";

/** Quiet zone in modules; the QR spec asks for 4. */
const QUIET = 4;

/** SVG path for the dark modules: one horizontal run per `M x y h w v1 h-w z`. */
export function qrPath(size: number, data: ArrayLike<number>): string {
  const parts: string[] = [];
  for (let y = 0; y < size; y++) {
    let x = 0;
    while (x < size) {
      if (!data[y * size + x]) {
        x++;
        continue;
      }
      const start = x;
      while (x < size && data[y * size + x]) x++;
      parts.push(`M${start + QUIET} ${y + QUIET}h${x - start}v1h-${x - start}z`);
    }
  }
  return parts.join("");
}

/**
 * Renders `value` as a QR code, client-side and offline (nothing is sent to a third party).
 * Always black on white, also in dark mode, so phone cameras read it reliably.
 */
export function QrCode(props: { value: string; label: string }): ReactNode {
  const { value, label } = props;
  const qr = useMemo(() => QRCode.create(value, { errorCorrectionLevel: "M" }), [value]);
  const size = qr.modules.size;
  const total = size + QUIET * 2;
  return (
    <span className="qr-frame">
      <svg
        className="qr"
        role="img"
        aria-label={label}
        viewBox={`0 0 ${total} ${total}`}
        shapeRendering="crispEdges"
        data-modules={size}
      >
        <rect width={total} height={total} fill="#fff" />
        <path d={qrPath(size, qr.modules.data)} fill="#000" />
      </svg>
    </span>
  );
}
